import { t } from "i18next";
import React, { useState, useRef, useEffect } from "react";
import { useAppSelector } from "store/hooks";
import { getSettings } from "store/slices";

export default function CustomSelect({
  options = [],
  value,
  onChange,
  placeholder = "Select...",
  disabled = false,
  className = "",
  style = {},
  searchOption = false,
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const { dir } = useAppSelector(getSettings);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const selectedOption = options.find((opt) => opt.value === value || opt.label === value);

  return (
    <div
      className={`relative min-w-[175px] ${className}`}
      style={style}
      ref={containerRef}
    >
      {/* Input box */}
      <div
        className={`flex items-center px-3 py-1 bg-white border rounded-[6px] cursor-pointer min-h-[30px] text-sm transition-all
          ${open ? "ring-2 ring-blue-400 border-blue-400" : "border-gray-300"}
          ${
            disabled
              ? "bg-gray-100 text-gray-400  font-heebo cursor-not-allowed"
              : "hover:border-blue-400"
          }
        `}
        onClick={() => !disabled && setOpen((v) => !v)}
        tabIndex={0}
        aria-disabled={disabled}
      >
        <span
          className={`flex-1 font-heebo ${
            !selectedOption ? "text-gray-400" : "text-gray-700"
          }`}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <svg
          className={`mx-2 w-4 h-4 text-gray-400 font-heebo transition-transform ${
            open ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>
      {/* Dropdown */}
      {open && !disabled && (
        <div className="absolute left-0 right-0 mt-1 bg-white shadow-lg rounded-[6px] border border-gray-100 z-10 overflow-auto max-h-60">
          {searchOption && (
            <div className="px-4 py-2">
              <input
                type="text"
                placeholder={t("search")}
                dir={dir}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border border-gray-300 rounded-md py-1 px-3 w-full font-heebo text-sm focus:outline-blue-400 focus:border-blue-400"
              />
            </div>
          )}
          {(searchOption ? options.filter((opt) => opt.label.toLowerCase().includes(searchTerm.toLowerCase())) : options).map((opt) => (
            <div
              key={opt.value}
              className={`px-4 py-2 cursor-pointer text-sm font-heebo
                ${
                  value === opt.value || value === opt.label
                    ? "bg-blue-100 font-bold text-gray-900"
                    : "hover:bg-gray-100 text-gray-700"
                }
              `}
              onClick={() => {
                if (opt.value !== value || opt.label !== value) onChange(opt.value, opt);
                setOpen(false);
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
